/**
 * Student-facing public share page — served at /s/:token
 *
 * Unauthenticated. Shown to the student when the counsellor sends
 * them their WUA shortlist. Reuses no Navbar / Footer to keep the
 * layout distraction-free and mobile-friendly. Bumps the view
 * counter on the backend every load so the counsellor can tell
 * when the student opened it.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Sparkles, TrendingUp, ExternalLink, Clock } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";


const REALISM = {
  safe: { chip: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Safe" },
  match: { chip: "bg-amber-50 text-amber-800 border-amber-200", label: "Match" },
  reach: { chip: "bg-rose-50 text-rose-800 border-rose-200", label: "Reach" },
  unknown: { chip: "bg-stone-50 text-stone-500 border-stone-200", label: "—" },
};


function RealismChip({ realism }) {
  const s = REALISM[realism] || REALISM.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${s.chip}`}
      data-testid={`share-realism-${realism || "unknown"}`}
    >
      {s.label}
    </span>
  );
}


function PickCard({ p, token }) {
  const logClick = () => {
    if (!p.degree_id || !token) return;
    // Fire-and-forget. Best-effort — use sendBeacon so the request
    // survives the tab context switch (new-tab navigation).
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/student-share/${token}/click`;
      const body = JSON.stringify({ degree_id: p.degree_id });
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
      }
    } catch (_) { /* silent */ }
  };

  return (
    <div
      data-testid={`share-pick-${p.degree_id || p.uni_id || p.uni_name}`}
      className="border border-stone-200 rounded-lg bg-white p-5 flex flex-col hover:border-stone-500 transition-colors"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="label-uppercase !text-[10px] text-stone-500">
            {p.country || "—"}
          </div>
          {p.uni_id ? (
            <Link to={`/uni/${p.uni_id}`} className="font-editorial text-xl mt-1 block hover:underline underline-offset-4 decoration-stone-400 leading-tight">
              {p.uni_name}
            </Link>
          ) : (
            <div className="font-editorial text-xl mt-1 leading-tight">{p.uni_name}</div>
          )}
          <div className="text-sm text-stone-700 mt-1">
            {p.degree_id ? (
              <Link
                to={`/degree/${p.degree_id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={logClick}
                onAuxClick={logClick}
                className="hover:underline underline-offset-4 decoration-stone-400 inline-flex items-center gap-1"
                data-testid={`share-degree-link-${p.degree_id}`}
              >
                {p.degree_name}
                <ExternalLink className="w-3 h-3 text-stone-400" />
              </Link>
            ) : (
              <span>{p.degree_name}</span>
            )}
          </div>
        </div>
        <RealismChip realism={p.entry_realism} />
      </div>
      {(p.reasons || []).length > 0 && (
        <ul className="mt-4 space-y-1.5 text-sm text-stone-700 leading-relaxed">
          {p.reasons.slice(0, 3).map((r, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-stone-400 mt-1">·</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}
      {p.leo_median_5yr_gbp && (
        <div className="mt-auto pt-4 flex items-center gap-2 text-sm text-stone-700">
          <TrendingUp className="w-4 h-4 text-stone-500" />
          Median 5-year salary: <span className="font-mono font-semibold">£{p.leo_median_5yr_gbp.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}


export default function StudentShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/student-share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer active."))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-poll while the counsellor's LLM analysis is still in flight —
  // typically the counsellor enables share + we auto-fire generation,
  // so the student may open the link ~10s before the write-up lands.
  useEffect(() => {
    if (!data || data.analysis_student || error) return;
    const id = setInterval(() => {
      api.get(`/public/student-share/${token}`)
        .then((r) => setData(r.data))
        .catch(() => {});
    }, 12000);
    return () => clearInterval(id);
  }, [data, error, token]);

  if (loading) {
    return <div className="max-w-3xl mx-auto p-12 text-sm text-stone-500">Loading your shortlist…</div>;
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center" data-testid="share-error">
        <AlertTriangle className="w-6 h-6 mx-auto text-stone-500" />
        <h1 className="font-editorial text-3xl mt-3">Link no longer active</h1>
        <p className="text-sm text-stone-600 mt-2">
          {error} If your counsellor sent you this link, ask them to send a fresh one.
        </p>
        <Link to="/" className="mt-6 inline-block text-sm underline underline-offset-4 decoration-stone-400">
          Explore WhichUniversityAdvisor →
        </Link>
      </div>
    );
  }

  const s = data.student;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="student-share-page">
      <SEOHead
        title={`${s.name} · Your WUA shortlist`}
        description="Personalised university shortlist prepared by your school counsellor with WUA outcomes overlay."
        path={`/s/${token}`}
      />

      {/* HEADER */}
      <div data-testid="share-header">
        <div className="label-uppercase text-stone-500">Personalised shortlist · prepared by your counsellor</div>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-[1.05]">
          Hi {s.first_name}, here's your <em>WUA shortlist.</em>
        </h1>
        <p className="mt-4 text-stone-700 max-w-2xl leading-relaxed">
          Six courses picked for your grades ({s.predicted_grades || "—"}), your subjects
          {s.subjects?.length ? ` (${s.subjects.join(", ")})` : ""},
          and — where you told us — your target country and career direction. Every course
          is tagged Safe · Match · Reach against your predictions, with graduate salary
          and employer signals so you can spot the ones actually worth taking.
        </p>
      </div>

      {/* ANALYSIS */}
      {data.analysis_student ? (
        <section
          className="mt-10 rounded-lg border border-stone-300 bg-[#F7F5F0] p-6 sm:p-8"
          data-testid="share-analysis"
        >
          <div className="flex items-center gap-2 text-stone-600">
            <Sparkles className="w-4 h-4" />
            <div className="label-uppercase !text-[10px]">A note from WUA</div>
          </div>
          <div className="mt-3 text-stone-800 leading-relaxed whitespace-pre-line max-w-3xl">
            {data.analysis_student}
          </div>
        </section>
      ) : (
        <section
          className="mt-10 rounded-lg border border-stone-200 bg-white p-6 sm:p-8"
          data-testid="share-analysis-pending"
        >
          <div className="flex items-center gap-2 text-stone-600">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <div className="label-uppercase !text-[10px]">Preparing your personal note</div>
          </div>
          <p className="mt-3 text-sm text-stone-600 leading-relaxed max-w-3xl">
            Your counsellor just enabled this link and WUA is generating your personal write-up
            right now (usually takes 30 seconds). Refresh this page in a minute to see it, or
            scroll down to browse your six matched courses in the meantime.
          </p>
        </section>
      )}

      {/* PICKS */}
      <section className="mt-12" data-testid="share-picks">
        <div className="flex items-baseline justify-between border-b border-stone-200 pb-3">
          <div>
            <div className="label-uppercase text-stone-500">Your six courses</div>
            <h2 className="font-editorial text-2xl sm:text-3xl mt-1">
              Ranked by fit, not by ranking.
            </h2>
          </div>
        </div>
        {data.picks.length === 0 ? (
          <div className="mt-6 text-sm text-stone-500 italic">
            Your counsellor hasn't finalised your matches yet. Please check back later today.
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            {data.picks.map((p, i) => <PickCard key={i} p={p} token={token} />)}
          </div>
        )}
      </section>

      {/* CTA */}
      <div
        className="mt-16 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
        data-testid="share-cta"
      >
        <div>
          <div className="label-uppercase text-stone-500">What next</div>
          <h3 className="font-editorial text-2xl mt-1">
            Explore any course in depth on WhichUniversityAdvisor.
          </h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            Click any university card above for the full outcomes / employer / cost breakdown, or
            search our full catalogue if you want to add options your counsellor didn't include.
          </p>
        </div>
        <Link
          to="/"
          data-testid="share-cta-explore"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Explore WUA <ExternalLink className="w-4 h-4" />
        </Link>
      </div>

      {/* Footer note */}
      <p className="mt-8 text-xs text-stone-500 italic flex items-center gap-2" data-testid="share-expires">
        <Clock className="w-3 h-3" />
        This personal link is private to you. It expires 90 days after your counsellor generated it.
      </p>
    </div>
  );
}
